import { alpha, Box, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { prepareFormElementProps } from 'components/common/form/formTools';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { components } from 'react-select';
import IconMi from 'components/common/icon/IconMi';
import { getAppBuilderCurrentStyleValue } from 'app/app/editor/tabs/settings/style-editor/StylesEditorContainer';
import { useAppBuilderContext } from 'app/app/editor/context';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';
interface ColorFieldProps {
    formState: any;
    propertyName: string;
    onChangeStyle: (value: any) => void;
    uid: string;
}
const colorOptionsListTheme = [
    {
        label: 'Primary',
        value: 'var(--theme-palette-primary-main)',
    },
    {
        label: 'Text',
        value: 'var(--theme-palette-text-primary)',
    },
    {
        label: 'Background',
        value: 'var(--theme-palette-background-default)',
    },
    {
        label: 'Accept',
        value: 'var(--theme-palette-success-main)',
    },
    {
        label: 'Alert: High',
        value: 'var(--theme-palette-error-main)',
    },
    {
        label: 'Alert: Medium',
        value: 'var(--theme-palette-warning-main)',
    },
    {
        label: 'Alert: Low',
        value: 'var(--theme-palette-warningLow-main)',
    },
    {
        label: 'Tab',
        value: 'var(--theme-component-tab-color)',
    },
    {
        label: 'Primary Button',
        value: 'var(--theme-component-primary-btn-background)',
    },
    {
        label: 'Primary Button Text',
        value: 'var(--theme-component-primary-btn-color)',
    },
    {
        label: 'Top Bar',
        value: 'var(--theme-component-topbar-background)',
    },
    {
        label: 'Top Bar Text',
        value: 'var(--theme-component-topbar-color)',
    },
];
const colorOptionsList = [
    {
        label: 'None',
        value: '',
    },
    {
        label: 'Custom',
        value: 'custom',
    },
];

const OptionComponent = (props: any) => {
    return (
        <components.Option {...props}>
            <Stack direction={'row'} spacing={0.5}>
                {props.value != '' && (
                    <>
                        {props.value == 'custom' ? (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '16px',
                                    height: '16px',
                                    fontSize: '16px',
                                }}
                            >
                                <IconMi icon={'color-picker'} />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: props.value,
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                }}
                            ></Box>
                        )}
                    </>
                )}

                <Box>{props.label}</Box>
            </Stack>
        </components.Option>
    );
};
const SelectedValueComponent = (props: any) => {
    let label = props.data.label;
    if (props.data.value != '' && props.data.value != 'custom') {
        const groupIndex = props.options.findIndex((group: any) => {
            return group?.options && group.options.find((item: any) => item.value == props.data.value);
        });
        if (groupIndex !== -1) label = props.options[groupIndex].label + ':  ' + label;
    }
    return (
        <components.SingleValue {...props}>
            <Stack direction={'row'} spacing={0.5}>
                {props.data.value != '' && (
                    <>
                        {props.data.value == 'custom' ? (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '16px',
                                    height: '16px',
                                    fontSize: '16px',
                                }}
                            >
                                <IconMi icon={'color-picker'} />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: '16px',
                                    height: '16px',
                                    backgroundColor: props.data.value,
                                    border: '1px solid',
                                    borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                                }}
                            ></Box>
                        )}
                    </>
                )}

                <Box>{label}</Box>
            </Stack>
        </components.SingleValue>
    );
};
export default function ColorField({ propertyName, formState, onChangeStyle = () => {}, uid }: ColorFieldProps) {
    const ctx = useAppBuilderContext();
    const node = uid == 'page' ? ctx.page.layout : getNodeByUid(ctx.page.layout, uid ?? '');
    const [currentColor, setCurrentColor] = useState<string>('');
    const [colorOptionsListFull, setColorOptionsListFull] = useState<any>(colorOptionsList);

    const elementProps = useCustomSimplifiedForm({
        colorField: '#222222',
    });
    const formValueColor = elementProps.form.hookFormWatch('colorField');

    useEffect(() => {
        let fullOptionsList: any = [...colorOptionsList];
        const colorVariables = (ctx.page?.cssVariables ?? []).filter((variable) => {
            return variable.type == 'color';
        });
        if (colorVariables.length) {
            const newVariableOptions = colorVariables.map((variable) => {
                return {
                    label: `${variable.title}`,
                    value: `var(--${variable.name})`,
                };
            });
            fullOptionsList.push({
                label: 'Style Variable',
                options: newVariableOptions,
            });
        }
        fullOptionsList.push({
            label: 'Theme Color',
            options: colorOptionsListTheme,
        });
        setColorOptionsListFull(fullOptionsList);

        if (node) {
            const colorVal = getAppBuilderCurrentStyleValue(node?.params?.layout ?? {}, propertyName);
            if (colorVal == '' || colorVal.startsWith('var(--')) {
                setCurrentColor(colorVal);
            } else {
                elementProps.form.hookFormSetValue('colorField', colorVal);
                setCurrentColor('custom');
            }
        }
    }, []);

    useEffect(() => {
        onChangeStyle({ [propertyName]: formValueColor });
    }, [formValueColor]);

    const handleChangeColorVariant = (value: any) => {
        setCurrentColor(value);
        if (value != 'custom') {
            onChangeStyle({ [propertyName]: value });
        } else {
            onChangeStyle({ [propertyName]: '#222222' });
        }
    };
    return (
        <>
            <ReactSelect
                data={colorOptionsListFull}
                value={[currentColor]}
                update={handleChangeColorVariant}
                overrideComponents={{
                    Option: OptionComponent,
                    SingleValue: SelectedValueComponent,
                }}
            />
            {currentColor == 'custom' && (
                <Box sx={{ pt: 1 }}>
                    <ReactHookFormController
                        elementProps={prepareFormElementProps({
                            ...elementProps,
                            component: {
                                component: 'ColorPicker',
                                name: 'colorField',
                                props: {
                                    disableAlpha: false,
                                },
                            },
                        })}
                    />
                </Box>
            )}
        </>
    );
}
